import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { adminUpload } from "@/lib/adminUpload";
import { Plus, Pencil, Trash2, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "@/components/MarkdownEditor";

const BUTTON_PRESETS = [
  "Register Now",
  "Buy Tickets",
  "RSVP",
  "Learn More",
  "Donate",
  "Watch Live",
  "Sign Up",
  "Custom…",
];

// Convert ISO with TZ → "YYYY-MM-DDTHH:MM" local datetime-input value (interpreted as EST/EDT)
function isoToLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // Get parts in America/New_York
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`;
  } catch { return ""; }
}

// Convert local "YYYY-MM-DDTHH:MM" (interpreted as America/New_York) → ISO UTC string
function localToIso(local) {
  if (!local) return "";
  // Build a date string and ask the browser to interpret it as US/Eastern.
  // Trick: format the local time as if it's UTC, then apply EST/EDT offset.
  const [datePart, timePart] = local.split("T");
  const [yr, mo, da] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  // Determine current EST/EDT offset (in minutes) for that date
  const probe = new Date(Date.UTC(yr, mo - 1, da, hh, mm));
  const tzName = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", timeZoneName: "short" }).format(probe);
  const offsetHours = tzName.includes("EDT") ? -4 : -5;
  const utc = new Date(Date.UTC(yr, mo - 1, da, hh - offsetHours, mm));
  return utc.toISOString();
}

const blank = {
  title: "", description: "", long_description: "", location: "",
  start_date: "", end_date: "", image_url: "", ticket_url: "",
  button_label: "Register Now", is_published: true, order: 0,
};

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [btnPreset, setBtnPreset] = useState("Register Now");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/events/"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({ ...blank });
    setBtnPreset("Register Now");
  };

  const startEdit = (it) => {
    setEditing({
      ...it,
      start_date: isoToLocal(it.start_date),
      end_date: isoToLocal(it.end_date),
    });
    setBtnPreset(BUTTON_PRESETS.includes(it.button_label) ? it.button_label : "Custom…");
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fullUrl = await adminUpload(file);
      setEditing((prev) => ({ ...prev, image_url: fullUrl }));
      toast.success("Flyer uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!editing.title || !editing.start_date) {
      return toast.error("Title and start date are required");
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        start_date: localToIso(editing.start_date),
        end_date: editing.end_date ? localToIso(editing.end_date) : "",
        order: Number(editing.order || 0),
      };
      if (editing.id) {
        await api.put(`/admin/events/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/admin/events/", payload);
        toast.success("Created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/admin/events/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Events</h1>
        </div>
        <button onClick={startNew} data-testid="events-new-btn" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900">
          <Plus size={14} /> New event
        </button>
      </div>

      {loading ? <div className="text-ink/40">Loading…</div> :
       items.length === 0 ? <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">No events yet.</div> : (
        <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-sand/50 text-left">
              <tr>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Flyer</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Title</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Start (EST)</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-sand hover:bg-cream/60">
                  <td className="px-5 py-3">
                    {it.image_url ? <img src={it.image_url} alt="" className="h-12 w-16 object-contain rounded bg-sand/40" /> : "—"}
                  </td>
                  <td className="px-5 py-3 text-ink font-medium">{it.title}</td>
                  <td className="px-5 py-3 text-ink/70 text-xs">
                    {new Date(it.start_date).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" })} EST
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase font-semibold rounded-full px-2.5 py-1 ${it.is_published ? "bg-navy/10 text-navy" : "bg-sand text-ink/50"}`}>
                      {it.is_published ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => startEdit(it)} className="p-2 text-ink/60 hover:text-navy"><Pencil size={14} /></button>
                    <button onClick={() => del(it.id)} className="p-2 text-ink/60 hover:text-destructive"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white border border-sand p-7 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit event" : "New event"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-ink/60 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="ev-title" />
              <Area label="Short description (1–2 lines, plain text)" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={2} testId="ev-desc" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Full description (rich text — supports formatting & images)</label>
                <div className="mt-2">
                  <MarkdownEditor
                    value={editing.long_description || ""}
                    onChange={(v) => setEditing({ ...editing, long_description: v })}
                    height={320}
                    testIdPrefix="ev-long"
                  />
                </div>
              </div>
              <Field label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testId="ev-location" />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Start (EST/EDT) *</label>
                  <input
                    type="datetime-local"
                    value={editing.start_date}
                    onChange={(e) => setEditing({ ...editing, start_date: e.target.value })}
                    data-testid="ev-start"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
                  />
                  <div className="text-[10px] text-ink/50 mt-1">Time entered as US Eastern time</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">End (EST/EDT)</label>
                  <input
                    type="datetime-local"
                    value={editing.end_date}
                    onChange={(e) => setEditing({ ...editing, end_date: e.target.value })}
                    data-testid="ev-end"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
                  />
                </div>
              </div>

              {/* Flyer upload */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Event flyer</label>
                <div className="mt-2 flex flex-wrap items-start gap-4">
                  {editing.image_url && (
                    <img src={editing.image_url} alt="flyer" className="w-32 h-44 object-contain rounded-xl bg-sand/40 border border-sand" />
                  )}
                  <div className="flex-1 min-w-[220px]">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => upload(e.target.files?.[0])}
                      className="hidden"
                      data-testid="ev-flyer-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="ev-flyer-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900 disabled:opacity-60"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {editing.image_url ? "Replace flyer" : "Upload flyer"}
                    </button>
                    <div className="text-[10px] text-ink/50 mt-2">JPG/PNG, up to 10MB. Or paste an external URL below.</div>
                    <input
                      type="text"
                      value={editing.image_url || ""}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                      placeholder="https://..."
                      data-testid="ev-flyer-url"
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-sand text-xs focus:outline-none focus:border-navy"
                    />
                  </div>
                </div>
              </div>

              {/* Button + URL */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Button label</label>
                  <select
                    value={btnPreset}
                    onChange={(e) => {
                      setBtnPreset(e.target.value);
                      if (e.target.value !== "Custom…") setEditing({ ...editing, button_label: e.target.value });
                    }}
                    data-testid="ev-btn-preset"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white"
                  >
                    {BUTTON_PRESETS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                  {btnPreset === "Custom…" && (
                    <input
                      type="text"
                      placeholder="Custom button label"
                      value={editing.button_label}
                      onChange={(e) => setEditing({ ...editing, button_label: e.target.value })}
                      data-testid="ev-btn-custom"
                      className="mt-2 w-full px-4 py-2.5 rounded-xl border border-sand text-sm focus:outline-none focus:border-navy"
                    />
                  )}
                </div>
                <Field label="Button link / URL" value={editing.ticket_url} onChange={(v) => setEditing({ ...editing, ticket_url: v })} placeholder="https://..." testId="ev-btn-url" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Order (lower = first)" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testId="ev-order" />
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-3 cursor-pointer pb-3">
                    <input
                      type="checkbox"
                      checked={!!editing.is_published}
                      onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                      data-testid="ev-published"
                    />
                    <span className="text-sm text-ink/75">Published (visible on public site)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full px-6 py-2.5 text-sm font-medium text-ink/70 hover:bg-sand/50">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="ev-save"
                      className="rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Save event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
      />
    </div>
  );
}

function Area({ label, value, onChange, rows = 4, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy resize-none"
      />
    </div>
  );
}
